/**
 * What the weather over the map sounds like: the rain, storm and wind levels for the ambience,
 * taken from the precipitation field under the part of the theatre the camera shows (nine
 * samples across the view), and the delay and weight of thunder after a lightning flash. Pure
 * (no Web Audio), so it is unit-tested.
 */
import {
  type GameMap,
  HEX_W,
  type HexGrid,
  hexTemperature,
  precipitationAt,
  SNOW_TEMP,
  type WeatherView,
} from '@friction/engine';
import type { CameraRect } from './intensity';

export interface WeatherMix {
  /** Rain on the roof, 0..1. */
  rain: number;
  /** The heavy roar of a storm, 0..1. */
  storm: number;
  /** Falling snow (it hushes rather than patters), 0..1. */
  snow: number;
  /** Wind strength 0..1 (prevailing wind, gusting up in storms and blizzards). */
  wind: number;
}

export const QUIET: WeatherMix = { rain: 0, storm: 0, snow: 0, wind: 0 };

const smooth = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

export function weatherMix(
  w: WeatherView | null,
  map: GameMap | null,
  grid: HexGrid | null,
  cam: CameraRect | null,
): WeatherMix {
  if (!w || !map || !grid) return QUIET;
  const rect = cam ?? { x: 0, y: 0, width: grid.worldWidth, height: grid.worldHeight };
  let rain = 0;
  let storm = 0;
  let snow = 0;
  for (let j = 0; j < 3; j++) {
    for (let i = 0; i < 3; i++) {
      const x = rect.x + rect.width * (0.2 + 0.3 * i);
      const y = rect.y + rect.height * (0.2 + 0.3 * j);
      const p = precipitationAt(w.cells, x / HEX_W, y / HEX_W);
      if (p <= 0) continue;
      const h = grid.fromWorld(x, y);
      const cold = hexTemperature(w.temperature, h >= 0 ? map.elevation[h] : 64) <= SNOW_TEMP;
      // The centre of the view counts double: that is where the listener leans in.
      const k = i === 1 && j === 1 ? 2 / 10 : 1 / 10;
      if (cold) snow += k * smooth(0.05, 0.7, p);
      else {
        rain += k * smooth(0.05, 0.6, p);
        storm += k * smooth(0.5, 0.95, p);
      }
    }
  }
  const breeze = Math.min(1, Math.hypot(w.wind.x, w.wind.y) / 0.9);
  const wind = Math.min(1, 0.35 * breeze + 0.8 * storm + 0.6 * snow);
  return { rain, storm, snow, wind };
}

/**
 * Thunder after a flash at world (x, y): seconds of delay (sound travels; the far side of the
 * view rumbles later) and loudness 0..1 (nearer is louder and sharper).
 */
export function thunder(x: number, y: number, cam: CameraRect | null): { delay: number; near: number } {
  if (!cam) return { delay: 1.5, near: 0.5 };
  const cx = cam.x + cam.width / 2;
  const cy = cam.y + cam.height / 2;
  const reach = Math.max(cam.width, cam.height) / 2;
  const d = Math.hypot(x - cx, y - cy) / Math.max(1, reach);
  return { delay: 0.25 + Math.min(4, d * 1.8), near: Math.max(0.15, 1 - d * 0.6) };
}
